import React, { useState, useEffect } from 'react';
import { Search, RefreshCw, Eye, Brain, Calendar, TrendingUp, User, CheckCircle2, XCircle, Settings, FileDown, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Loader from '../../components/ui/Loader';
import Badge from '../../components/ui/Badge';
import Card from '../../components/ui/Card';
import discService from '../../services/discService';
import perfilService from '../../services/perfilService';
import perfilPruebaService from '../../services/perfilPruebaService';
import { generarReporteDISC } from '../../utils/pdfGenerator';
import useAuthStore from '../../store/authStore';

export default function ExamenesDisc() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [testsDisc, setTestsDisc] = useState([]);
  const [perfiles, setPerfiles] = useState([]);
  const [asociaciones, setAsociaciones] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTestDisc, setSelectedTestDisc] = useState(null);
  const [showAsignacionModal, setShowAsignacionModal] = useState(false);
  const [discExamenes, setDiscExamenes] = useState([]);
  const [creandoExamenMaestro, setCreandoExamenMaestro] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const testsDiscData = await discService.getTodosLosTestsCompletados();
      setTestsDisc(testsDiscData || []);
      
      const perfilesData = await perfilService.getAllPerfiles();
      setPerfiles(perfilesData || []);
      
      const asociacionesData = await perfilPruebaService.getAll();
      setAsociaciones(asociacionesData || []);

      // Cargar los DiscExamenes maestros
      try {
        const discExamenesData = await discService.listarDiscExamenes();
        setDiscExamenes(discExamenesData || []);
      } catch (error) {
        console.error('Error cargando DiscExamenes:', error);
        setDiscExamenes([]);
      }
    } catch (error) {
      console.error('Error cargando tests DISC:', error);
      toast.error('Error al cargar tests DISC');
      setTestsDisc([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCrearExamenMaestro = async () => {
    try {
      setCreandoExamenMaestro(true);
      toast.loading('Creando examen maestro DISC...', { id: 'crear-maestro-disc' });
      
      // Crear el examen maestro con el ID del usuario actual
      const nuevoExamen = await discService.crearDiscExamen(user.id);
      
      toast.success(`Examen maestro DISC creado con ID: ${nuevoExamen.id}`, { id: 'crear-maestro-disc' });
      
      await loadData();
    } catch (error) {
      console.error('Error creando examen maestro DISC:', error);
      toast.error('Error al crear el examen maestro DISC', { id: 'crear-maestro-disc' });
    } finally {
      setCreandoExamenMaestro(false);
    }
  };

  const filteredTests = testsDisc.filter(test =>
    test.usuarioNombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    test.usuarioDni?.includes(searchTerm) ||
    test.perfilResultado?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleTogglePerfil = async (perfilId, isAssigned) => {
    try {
      if (isAssigned) {
        // Encontrar y eliminar la asociación
        const asociacion = asociaciones.find(
          a => a.testDiscId != null && a.perfilId === perfilId
        );
        if (asociacion) {
          await perfilPruebaService.delete(asociacion.id);
          toast.success('Test DISC desasignado del perfil');
        }
      } else {
        // Crear nueva asociación (usando discExamenId = 1)
        await perfilPruebaService.asociarDiscExamen(perfilId, 1);
        toast.success('Test DISC asignado al perfil');
      }
      loadData();
    } catch (error) {
      console.error('Error al actualizar asociación:', error);
      toast.error('Error al actualizar asignación');
    }
  };

  const getPerfilesAsignados = () => {
    return asociaciones
      .filter(a => a.testDiscId != null)
      .map(a => {
        const perfil = perfiles.find(p => p.id === a.perfilId);
        return perfil ? perfil.nombre : `Perfil #${a.perfilId}`;
      });
  };

  const handleExportarPDF = async (testDisc) => {
    try {
      toast.loading('Generando reporte PDF...', { id: 'pdf-disc' });
      await generarReporteDISC(testDisc);
      toast.success('Reporte DISC generado exitosamente', { id: 'pdf-disc' });
    } catch (error) {
      console.error('Error generando PDF:', error);
      toast.error('Error al generar el reporte PDF', { id: 'pdf-disc' });
    }
  };

  // Verificar si existe el examen maestro con ID 1
  const existeExamenMaestro = discExamenes.some(e => e.id === 1);

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-neutral-800">Tests DISC</h1>
              <p className="text-neutral-600 mt-1">Resultados de evaluaciones de personalidad</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {!existeExamenMaestro && (
            <Button
              onClick={handleCrearExamenMaestro}
              disabled={creandoExamenMaestro}
              className="gap-2 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700"
            >
              <Plus className="w-4 h-4" />
              {creandoExamenMaestro ? 'Creando...' : 'Crear Examen Maestro DISC'}
            </Button>
          )}
          {existeExamenMaestro && (
            <Button 
              onClick={() => setShowAsignacionModal(true)} 
              className="gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Settings className="w-4 h-4" />
              Asignar a Perfiles
            </Button>
          )}
        </div>
      </div>

      {/* Alerta si no existe el examen maestro */}
      {!existeExamenMaestro && (
        <Card className="border-l-4 border-l-red-500 bg-red-50">
          <div className="p-4">
            <div className="flex items-start gap-3">
              <Brain className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-800 mb-1">⚠️ Examen Maestro DISC No Existe</h3>
                <p className="text-sm text-red-700 mb-3">
                  El sistema DISC requiere un examen maestro con <strong>ID = 1</strong> para funcionar correctamente.
                  Este examen se utiliza como referencia para asignar el Test DISC a los perfiles.
                </p>
                <div className="bg-red-100 rounded-lg p-3 mt-2">
                  <p className="text-xs text-red-800">
                    <strong>📋 Acción requerida:</strong> Haz clic en el botón "Crear Examen Maestro DISC" 
                    para generar el examen con ID 1. Una vez creado, podrás asignarlo a los perfiles deseados.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Card de perfiles asignados */}
      {existeExamenMaestro && (
        <Card className="border-l-4 border-l-purple-500">
          <div className="p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-neutral-800 mb-2">Perfiles con Test DISC Habilitado</h3>
                {getPerfilesAsignados().length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {getPerfilesAsignados().map((nombre, idx) => (
                      <Badge key={idx} variant="primary" className="bg-gradient-to-r from-white-600 to-pink-600">
                        {nombre}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500 italic">
                    No hay perfiles con Test DISC habilitado. Haz clic en "Asignar a Perfiles" para configurar.
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Tarjeta Informativa del Test DISC */}
      <Card className="overflow-hidden hover:shadow-lg transition-shadow">
        <div className="flex flex-col md:flex-row">
          {/* Imagen */}
          <div className="md:w-1/3 h-48 md:h-auto bg-gradient-to-br from-white-500 to-teal-600 flex items-center justify-center relative overflow-hidden">
            <img 
              src="/assets/disc-test.png" 
              alt="Test DISC" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center" style={{display: 'none'}}>
              <div className="grid grid-cols-2 gap-3">
                <div className="w-14 h-14 rounded-full bg-red-400/90 flex items-center justify-center text-white font-bold text-xl shadow-lg">D</div>
                <div className="w-14 h-14 rounded-full bg-yellow-400/90 flex items-center justify-center text-white font-bold text-xl shadow-lg">I</div>
                <div className="w-14 h-14 rounded-full bg-green-400/90 flex items-center justify-center text-white font-bold text-xl shadow-lg">S</div>
                <div className="w-14 h-14 rounded-full bg-blue-400/90 flex items-center justify-center text-white font-bold text-xl shadow-lg">C</div>
              </div>
            </div>
            <div className="absolute top-3 right-3">
              <Badge className="bg-white/20 text-white border-white/30">Personalidad</Badge>
            </div>
          </div>
          
          {/* Contenido */}
          <div className="md:w-2/3 p-6">
            <h3 className="text-2xl font-bold text-neutral-800 mb-3 flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500" />
              Test de Personalidad DISC
            </h3>
            <p className="text-neutral-600 mb-4">
              Herramienta de evaluación conductual que identifica cuatro tipos de comportamiento predominantes. 
              Basado en la teoría de William Marston, permite comprender el estilo de trabajo y comunicación de cada persona.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Las 4 Dimensiones</h4>
                <ul className="space-y-1 text-sm text-neutral-600">
                  <li className="flex items-center gap-2">
                    <span className="w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold">D</span>
                    <span><strong>Dominancia:</strong> Decisión, control</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-4 h-4 bg-yellow-500 rounded-full text-white text-xs flex items-center justify-center font-bold">I</span>
                    <span><strong>Influencia:</strong> Comunicación, entusiasmo</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-4 h-4 bg-green-500 rounded-full text-white text-xs flex items-center justify-center font-bold">S</span>
                    <span><strong>Estabilidad:</strong> Paciencia, cooperación</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-4 h-4 bg-blue-500 rounded-full text-white text-xs flex items-center justify-center font-bold">C</span>
                    <span><strong>Cumplimiento:</strong> Precisión, calidad</span>
                  </li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Características</h4>
                <ul className="space-y-1 text-sm text-neutral-600">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                    28 preguntas de selección
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                    Duración: 15-20 minutos
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                    Resultado: Perfil + Gráfico
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                    Genera reporte PDF detallado
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3 pt-4 border-t border-neutral-100">
              <div className="flex items-center gap-2 text-xs bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full">
                <span>👥</span> Mejora equipos de trabajo
              </div>
              <div className="flex items-center gap-2 text-xs bg-teal-50 text-teal-700 px-3 py-1.5 rounded-full">
                <span>💼</span> Ideal para selección
              </div>
              <div className="flex items-center gap-2 text-xs bg-cyan-50 text-cyan-700 px-3 py-1.5 rounded-full">
                <span>📊</span> Análisis de fortalezas
              </div>
            </div>
          </div>
        </div>
      </Card>
      {/*
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-4">
        <div className="flex-1 sm:max-w-md">
          <Input
            placeholder="Buscar por usuario, DNI o perfil..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
        </div>
        <Button variant="outline" onClick={loadData} className="gap-2 w-full sm:w-auto justify-center">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

       Vista Desktop - Tabla 
      <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-neutral-200">
            <tr>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Usuario</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">DNI</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Perfil Resultado</th>
              <th className="text-center p-4 text-sm font-semibold text-neutral-700">Puntuaciones DISC</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Segmento</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Fecha</th>
              <th className="text-right p-4 text-sm font-semibold text-neutral-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {filteredTests.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-8 text-center text-neutral-500">
                  <Brain className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
                  <p>No se encontraron tests DISC completados</p>
                </td>
              </tr>
            ) : (
              filteredTests.map((testDisc) => (
                <tr key={testDisc.id} className="hover:bg-neutral-50">
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="font-medium text-neutral-800">{testDisc.usuarioNombre}</span>
                    </div>
                  </td>
                  <td className="p-4 text-neutral-600">{testDisc.usuarioDni}</td>
                  <td className="p-4">
                    <Badge variant="primary" className="bg-gradient-to-r from-purple-600 to-pink-600">
                      {testDisc.perfilResultado}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-center gap-2">
                      <div className="bg-red-50 border border-red-200 rounded px-2 py-1 text-center min-w-[45px]">
                        <p className="text-xs text-red-600 font-medium">D</p>
                        <p className="text-sm font-bold text-red-700">{testDisc.puntuacionD}</p>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded px-2 py-1 text-center min-w-[45px]">
                        <p className="text-xs text-yellow-600 font-medium">I</p>
                        <p className="text-sm font-bold text-yellow-700">{testDisc.puntuacionI}</p>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded px-2 py-1 text-center min-w-[45px]">
                        <p className="text-xs text-green-600 font-medium">S</p>
                        <p className="text-sm font-bold text-green-700">{testDisc.puntuacionS}</p>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1 text-center min-w-[45px]">
                        <p className="text-xs text-blue-600 font-medium">C</p>
                        <p className="text-sm font-bold text-blue-700">{testDisc.puntuacionC}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2 text-neutral-600">
                      <TrendingUp className="w-4 h-4" />
                      <span className="font-medium">{testDisc.segmento}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2 text-neutral-600">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">{new Date(testDisc.fechaRealizacion).toLocaleDateString('es-PE')}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedTestDisc(testDisc)}
                        title="Ver detalles completos"
                      >
                        <Eye className="w-4 h-4" />
                        Ver Detalle
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleExportarPDF(testDisc)}
                        title="Exportar reporte PDF"
                      >
                        <FileDown className="w-4 h-4" />
                        PDF
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Vista Mobile/Tablet - Cards *
      <div className="lg:hidden space-y-3">
        {filteredTests.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8 text-center text-neutral-500">
            <Brain className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
            <p>No se encontraron tests DISC completados</p>
          </div>
        ) : (
          filteredTests.map((testDisc) => (
            <div key={testDisc.id} className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-800">{testDisc.usuarioNombre}</h3>
                    <p className="text-sm text-neutral-500">{testDisc.usuarioDni}</p>
                  </div>
                </div>
                <Badge variant="primary" className="bg-gradient-to-r from-purple-600 to-pink-600">
                  {testDisc.perfilResultado}
                </Badge>
              </div>

              {/* Puntuaciones DISC *
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="bg-red-50 border border-red-200 rounded px-2 py-1 text-center">
                  <p className="text-xs text-red-600 font-medium">D</p>
                  <p className="text-sm font-bold text-red-700">{testDisc.puntuacionD}</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded px-2 py-1 text-center">
                  <p className="text-xs text-yellow-600 font-medium">I</p>
                  <p className="text-sm font-bold text-yellow-700">{testDisc.puntuacionI}</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded px-2 py-1 text-center">
                  <p className="text-xs text-green-600 font-medium">S</p>
                  <p className="text-sm font-bold text-green-700">{testDisc.puntuacionS}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1 text-center">
                  <p className="text-xs text-blue-600 font-medium">C</p>
                  <p className="text-sm font-bold text-blue-700">{testDisc.puntuacionC}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 text-sm text-neutral-600 mb-4">
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-4 h-4" />
                  <span>{testDisc.segmento}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(testDisc.fechaRealizacion).toLocaleDateString('es-PE')}</span>
                </div>
              </div>

              <div className="flex gap-2 pt-3 border-t border-neutral-100">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedTestDisc(testDisc)}
                  className="flex-1 justify-center"
                >
                  <Eye className="w-4 h-4" />
                  <span className="ml-1">Ver Detalle</span>
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleExportarPDF(testDisc)}
                  className="flex-1 justify-center"
                >
                  <FileDown className="w-4 h-4" />
                  <span className="ml-1">PDF</span>
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de detalles del test DISC */}
      {selectedTestDisc && (
        <Modal
          isOpen={!!selectedTestDisc}
          onClose={() => setSelectedTestDisc(null)}
          title={`Test DISC - ${selectedTestDisc.usuarioNombre}`}
          size="xl"
        >
          <div className="space-y-6">
            {/* Resumen */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-neutral-600">DNI</p>
                <p className="font-semibold text-neutral-900">{selectedTestDisc.usuarioDni}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-600">Perfil Resultado</p>
                <Badge variant="primary" className="bg-gradient-to-r from-purple-600 to-pink-600 mt-1">
                  {selectedTestDisc.perfilResultado}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-neutral-600">Segmento</p>
                <p className="font-semibold text-neutral-900">{selectedTestDisc.segmento}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-600">Fecha</p>
                <p className="font-semibold text-neutral-900">
                  {new Date(selectedTestDisc.fechaRealizacion).toLocaleString('es-PE')}
                </p>
              </div>
            </div>

            {/* Puntuaciones detalladas */}
            <div>
              <h3 className="font-bold text-neutral-900 mb-3">Puntuaciones DISC</h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-red-600 font-medium mb-2">Dominancia</p>
                  <p className="text-3xl font-bold text-red-700">{selectedTestDisc.puntuacionD}</p>
                </div>
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-yellow-600 font-medium mb-2">Influencia</p>
                  <p className="text-3xl font-bold text-yellow-700">{selectedTestDisc.puntuacionI}</p>
                </div>
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-green-600 font-medium mb-2">Estabilidad</p>
                  <p className="text-3xl font-bold text-green-700">{selectedTestDisc.puntuacionS}</p>
                </div>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-blue-600 font-medium mb-2">Cumplimiento</p>
                  <p className="text-3xl font-bold text-blue-700">{selectedTestDisc.puntuacionC}</p>
                </div>
              </div>
            </div>

            {/* Descripciones */}
            <div className="space-y-3">
              <h3 className="font-bold text-neutral-900">Análisis del Perfil</h3>
              
              {selectedTestDisc.descripcionCompleta && (
                <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-300">
                  <p className="text-sm font-bold text-purple-700 mb-2">Descripción Completa del Perfil:</p>
                  <p className="text-neutral-900 leading-relaxed">{selectedTestDisc.descripcionCompleta}</p>
                </div>
              )}
              
              {selectedTestDisc.descripcionEmociones && (
                <div className="bg-neutral-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-neutral-700 mb-1">Emociones:</p>
                  <p className="text-neutral-900">{selectedTestDisc.descripcionEmociones}</p>
                </div>
              )}
              
              {selectedTestDisc.descripcionMeta && (
                <div className="bg-neutral-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-neutral-700 mb-1">Meta:</p>
                  <p className="text-neutral-900">{selectedTestDisc.descripcionMeta}</p>
                </div>
              )}
              
              {selectedTestDisc.descripcionJuzga && (
                <div className="bg-neutral-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-neutral-700 mb-1">Juzga a otros por:</p>
                  <p className="text-neutral-900">{selectedTestDisc.descripcionJuzga}</p>
                </div>
              )}
              
              {selectedTestDisc.descripcionInfluye && (
                <div className="bg-neutral-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-neutral-700 mb-1">Influye mediante:</p>
                  <p className="text-neutral-900">{selectedTestDisc.descripcionInfluye}</p>
                </div>
              )}
              
              {selectedTestDisc.descripcionValor && (
                <div className="bg-neutral-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-neutral-700 mb-1">Su valor para la organización:</p>
                  <p className="text-neutral-900">{selectedTestDisc.descripcionValor}</p>
                </div>
              )}
              
              {selectedTestDisc.descripcionTeme && (
                <div className="bg-neutral-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-neutral-700 mb-1">Teme:</p>
                  <p className="text-neutral-900">{selectedTestDisc.descripcionTeme}</p>
                </div>
              )}
              
              {selectedTestDisc.descripcionEficaz && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <p className="text-sm font-medium text-green-700 mb-1">Sería más eficaz si:</p>
                  <p className="text-neutral-900">{selectedTestDisc.descripcionEficaz}</p>
                </div>
              )}
            </div>

            {/* Archivo Excel si existe */}
            {selectedTestDisc.archivoExcelPath && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-sm font-medium text-blue-700 mb-1">Archivo de resultados:</p>
                <p className="text-neutral-900">{selectedTestDisc.archivoExcelPath}</p>
              </div>
            )}

            {/* Botón para exportar PDF */}
            <div className="flex justify-end pt-4 border-t">
              <Button
                onClick={() => handleExportarPDF(selectedTestDisc)}
                className="gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              >
                <FileDown className="w-4 h-4" />
                Exportar Reporte PDF
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de asignación a perfiles */}
      <AsignacionPerfilesDiscModal
        isOpen={showAsignacionModal}
        onClose={() => setShowAsignacionModal(false)}
        perfiles={perfiles}
        asociaciones={asociaciones}
        onTogglePerfil={handleTogglePerfil}
      />
    </div>
  );
}

function AsignacionPerfilesDiscModal({ isOpen, onClose, perfiles, asociaciones, onTogglePerfil }) {
  const isPerfilAsignado = (perfilId) => {
    return asociaciones.some(
      a => a.testDiscId != null && a.perfilId === perfilId
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Asignar Test DISC a Perfiles"
      size="lg"
    >
      <div className="space-y-4">
        <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Brain className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
            <div>
              <p className="text-sm text-purple-900 font-medium mb-2">
                <strong>Test DISC de 28 Preguntas</strong>
              </p>
              <p className="text-xs text-purple-700">
                Selecciona los perfiles que podrán realizar el Test DISC. Los usuarios asignados a estos perfiles 
                verán el test disponible en su panel con las 28 preguntas estándar de evaluación de personalidad.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          {perfiles.length === 0 ? (
            <p className="text-center text-neutral-500 py-8">No hay perfiles disponibles</p>
          ) : (
            perfiles.map((perfil) => {
              const isAsignado = isPerfilAsignado(perfil.id);
              
              return (
                <div
                  key={perfil.id}
                  onClick={() => onTogglePerfil(perfil.id, isAsignado)}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    isAsignado
                      ? 'border-purple-500 bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100'
                      : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                  }`}
                >
                  <div className="flex-1">
                    <h3 className={`font-semibold ${isAsignado ? 'text-purple-800' : 'text-neutral-800'}`}>
                      {perfil.nombre}
                    </h3>
                    <p className={`text-sm ${isAsignado ? 'text-purple-600' : 'text-neutral-600'}`}>
                      {perfil.descripcion || 'Sin descripción'}
                    </p>
                  </div>
                  <div className="ml-4">
                    {isAsignado ? (
                      <CheckCircle2 className="w-6 h-6 text-purple-600" />
                    ) : (
                      <XCircle className="w-6 h-6 text-neutral-400" />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button 
            onClick={onClose} 
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
